import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, IMPIANTI, AZIENDA } from "@/lib/config";
import { Stelle } from "@/components/Stelle";
import { ShareButtons } from "@/components/ShareButtons";

const Box = ({ children, testid }) => (
  <div className="min-h-screen grid place-items-center bg-slate-50 water-bg px-4 py-10" data-testid={testid}>
    <div className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">{children}</div>
  </div>
);

export default function LasciaRecensione() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [errore, setErrore] = useState("");
  const [voto, setVoto] = useState(0);
  const [testo, setTesto] = useState("");
  const [nome, setNome] = useState("");
  const [loading, setLoading] = useState(false);
  const [fatto, setFatto] = useState(false);

  useEffect(() => {
    axios.get(`${API}/recensioni/invito/${token}`)
      .then((r) => { setInfo(r.data); setNome(r.data.nome); })
      .catch((e) => setErrore(e.response?.data?.detail || "Link non valido"));
  }, [token]);

  const invia = async (e) => {
    e.preventDefault();
    if (!voto) return toast.error("Seleziona da 1 a 5 stelle");
    setLoading(true);
    try {
      await axios.post(`${API}/recensioni`, { token, voto, testo, nome_visibile: nome });
      setFatto(true);
    } catch (err) {
      toast.error(err.response?.data?.detail === undefined ? "Errore, riprova" : typeof err.response.data.detail === "string" ? err.response.data.detail : "Scrivi almeno 10 caratteri");
    } finally { setLoading(false); }
  };

  const torna = <Link to="/" className="mt-6 inline-block rounded-full bg-sky-600 text-white px-6 py-3 font-bold hover:bg-sky-700" data-testid="recensione-home-link">Vai al sito</Link>;

  if (errore) return <Box testid="recensione-error"><AlertTriangle className="w-10 h-10 text-orange-500" /><h1 className="mt-3 text-xl font-bold">Link non disponibile</h1><p className="mt-2 text-slate-600">{errore}</p>{torna}</Box>;
  if (!info) return <Box testid="recensione-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto text-sky-600" /></Box>;
  if (info.in_attesa) return (
    <Box testid="recensione-in-attesa"><CheckCircle2 className="w-12 h-12 text-sky-600" /><h1 className="mt-3 text-2xl font-extrabold">Ciao {info.nome}!</h1><p className="mt-2 text-slate-600">Questo è il tuo link personale per recensire l'intervento "{info.tipo_problema}". Potrai lasciare la recensione appena l'intervento sarà completato: riapri questo stesso link.</p>{torna}</Box>
  );
  if (fatto || info.recensito) return (
    <Box testid="recensione-done"><CheckCircle2 className="w-12 h-12 text-emerald-600" /><h1 className="mt-3 text-2xl font-extrabold">Grazie{fatto ? ` ${nome}` : ""}!</h1><p className="mt-2 text-slate-600">{fatto ? "La tua recensione verificata è stata pubblicata." : "Hai già lasciato una recensione per questo intervento."}</p>{fatto && (<div className="mt-6 space-y-3" data-testid="recensione-share"><p className="font-semibold">Ti sei trovato bene? Consigliaci ad amici e vicini:</p><ShareButtons idPrefix="recensione-share" testo={`Mi sono trovato bene con questo idraulico (${voto} stelle): pronto intervento H24 in provincia di Rimini.`} /></div>)}{torna}</Box>
  );

  return (
    <Box testid="recensione-page">
      <p className="text-xs font-bold uppercase tracking-wider text-sky-700">{AZIENDA.nome}</p>
      <h1 className="mt-2 text-2xl font-extrabold tracking-tight">Ciao {info.nome}, com'è andato l'intervento?</h1>
      <p className="mt-2 text-slate-600">Intervento: <b>{info.tipo_problema}</b> · {IMPIANTI[info.impianto]}</p>
      <form onSubmit={invia} className="mt-6 space-y-5" data-testid="recensione-form">
        <div><p className="font-semibold mb-1">Il tuo voto</p><Stelle voto={voto} size="w-8 h-8" onChange={setVoto} testidPrefix="recensione-stella" /></div>
        <div className="space-y-2"><p className="font-semibold">La tua esperienza</p><Textarea required minLength={10} rows={4} value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Puntualità, cortesia, qualità del lavoro..." data-testid="recensione-testo-input" /></div>
        <div className="space-y-2"><p className="font-semibold">Nome da mostrare</p><Input required value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Mario R." className="h-12" data-testid="recensione-nome-input" /></div>
        <button disabled={loading} className="w-full h-12 rounded-full bg-sky-600 text-white font-bold hover:bg-sky-700 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="recensione-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Pubblica recensione
        </button>
      </form>
    </Box>
  );
}
